import { computed, inject, Signal } from '@angular/core';

import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { tapResponse } from '@ngrx/operators';
import { signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { filter, mergeMap, pipe, switchMap } from 'rxjs';

import {
  BillingModel,
  PartnerCustomerListItemModel,
  PartnerCustomerOverviewModel,
  PartnerCustomerPricingModel,
  PartnerCustomerProductStateModel,
  PartnerCustomerProductType,
  PartnerPortalService,
} from '@bn/openapi/partner-portal';
import { ApiErrorResponse } from '@bn/primitives';
import { BnToastrService } from '@bn/shared/toastr';
import { getStoreEvents, StoreRequest, withDefaultRequestStatusState } from '@bn/store-kit/v1';

import { environment } from '../../../../environments/environment';
import { downloadFile } from '../../../core/utils';

const events = getStoreEvents('PARTNER_CUSTOMER', ['OVERVIEW', 'PRODUCTS', 'PRICING', 'BILLING', 'BILLING_PDF', 'IMPERSONATE', 'RESET']);

export type PartnerCustomerRequestKey = 'overview' | 'products' | 'pricing' | 'billing';
export type PartnerCustomerRequestStatus = 'idle' | 'loading' | 'finished' | 'error';

export interface PartnerCustomerModel {
  customers: PartnerCustomerListItemModel[];
  selectedCustomerId: string | null;
  overview: PartnerCustomerOverviewModel | null;
  products: PartnerCustomerProductStateModel[];
  pricing: PartnerCustomerPricingModel | null;
  billing: BillingModel | null;
  requestStatus: Record<PartnerCustomerRequestKey, PartnerCustomerRequestStatus>;
}

const initialState: PartnerCustomerModel = {
  customers: [],
  selectedCustomerId: null,
  overview: null,
  products: [],
  pricing: null,
  billing: null,
  requestStatus: { overview: 'idle', products: 'idle', pricing: 'idle', billing: 'idle' },
};

function setRequestStatus(
  current: Record<PartnerCustomerRequestKey, PartnerCustomerRequestStatus>,
  key: PartnerCustomerRequestKey,
  status: PartnerCustomerRequestStatus,
): Pick<PartnerCustomerModel, 'requestStatus'> {
  return { requestStatus: { ...current, [key]: status } };
}

function mapProductEnabled(
  products: PartnerCustomerProductStateModel[],
  productId: PartnerCustomerProductType,
  enabled: boolean,
): PartnerCustomerProductStateModel[] {
  return products.map((product) => (product.ProductId === productId ? { ...product, IsEnabled: enabled } : product));
}

export const PartnerCustomerStore = signalStore(
  withDevtools('PartnerPortal.Customer'),
  withDefaultRequestStatusState(),
  withState<PartnerCustomerModel>(initialState),
  withMethods((store, partnerPortalService = inject(PartnerPortalService), toastr = inject(BnToastrService)) => ({
    selectCustomer(customerUserId: string): void {
      updateState(store, events.SELECT.SUCCESS, { selectedCustomerId: customerUserId });
    },

    isRequestLoading(key: PartnerCustomerRequestKey): Signal<boolean> {
      return computed(() => {
        const status = store.requestStatus()[key];
        return status === 'loading' || status === 'idle';
      });
    },

    isRequestError(key: PartnerCustomerRequestKey): Signal<boolean> {
      return computed(() => store.requestStatus()[key] === 'error');
    },

    loadCustomers: rxMethod<void>(
      switchMap(() => {
        updateState(store, events.LIST.REQUEST, store.setLoading(StoreRequest.List));
        return partnerPortalService.listPartnerCustomersEndpointListCustomers().pipe(
          tapResponse({
            next: (customers: PartnerCustomerListItemModel[]) =>
              updateState(
                store,
                events.LIST.SUCCESS,
                { customers, selectedCustomerId: store.selectedCustomerId() ?? customers[0]?.CustomerUserId ?? null },
                store.setFinished(StoreRequest.List),
              ),
            error: (error: ApiErrorResponse) => updateState(store, events.LIST.ERROR, store.setError(StoreRequest.List, error)),
          }),
        );
      }),
    ),

    loadOverview: rxMethod<string | null>(
      pipe(
        filter((customerUserId): customerUserId is string => !!customerUserId),
        switchMap((customerUserId) => {
          updateState(store, events.OVERVIEW.REQUEST, setRequestStatus(store.requestStatus(), 'overview', 'loading'));
          return partnerPortalService.getPartnerCustomerOverviewEndpointGetOverview({ customerUserId }).pipe(
            tapResponse({
              next: (overview: PartnerCustomerOverviewModel) =>
                updateState(store, events.OVERVIEW.SUCCESS, { overview }, setRequestStatus(store.requestStatus(), 'overview', 'finished')),
              error: () => updateState(store, events.OVERVIEW.ERROR, setRequestStatus(store.requestStatus(), 'overview', 'error')),
            }),
          );
        }),
      ),
    ),

    loadProducts: rxMethod<string | null>(
      pipe(
        filter((customerUserId): customerUserId is string => !!customerUserId),
        switchMap((customerUserId) => {
          updateState(store, events.PRODUCTS.REQUEST, setRequestStatus(store.requestStatus(), 'products', 'loading'));
          return partnerPortalService.getPartnerCustomerProductsEndpointGetProducts({ customerUserId }).pipe(
            tapResponse({
              next: (products: PartnerCustomerProductStateModel[]) =>
                updateState(store, events.PRODUCTS.SUCCESS, { products }, setRequestStatus(store.requestStatus(), 'products', 'finished')),
              error: () => updateState(store, events.PRODUCTS.ERROR, setRequestStatus(store.requestStatus(), 'products', 'error')),
            }),
          );
        }),
      ),
    ),

    loadPricing: rxMethod<string | null>(
      pipe(
        filter((customerUserId): customerUserId is string => !!customerUserId),
        switchMap((customerUserId) => {
          updateState(store, events.PRICING.REQUEST, { pricing: null }, setRequestStatus(store.requestStatus(), 'pricing', 'loading'));
          return partnerPortalService.getPartnerCustomerPricingEndpointGetPricing({ customerUserId }).pipe(
            tapResponse({
              next: (pricing: PartnerCustomerPricingModel) =>
                updateState(store, events.PRICING.SUCCESS, { pricing }, setRequestStatus(store.requestStatus(), 'pricing', 'finished')),
              error: () => updateState(store, events.PRICING.ERROR, setRequestStatus(store.requestStatus(), 'pricing', 'error')),
            }),
          );
        }),
      ),
    ),

    loadBilling: rxMethod<string | null>(
      pipe(
        filter((customerUserId): customerUserId is string => !!customerUserId),
        switchMap((customerUserId) => {
          updateState(store, events.BILLING.REQUEST, { billing: null }, setRequestStatus(store.requestStatus(), 'billing', 'loading'));
          return partnerPortalService.getPartnerCustomerBillingEndpointGetBilling({ customerUserId }).pipe(
            tapResponse({
              next: (billing: BillingModel) =>
                updateState(store, events.BILLING.SUCCESS, { billing }, setRequestStatus(store.requestStatus(), 'billing', 'finished')),
              error: () => updateState(store, events.BILLING.ERROR, setRequestStatus(store.requestStatus(), 'billing', 'error')),
            }),
          );
        }),
      ),
    ),

    setProductEnabled: rxMethod<{ customerUserId: string; productId: PartnerCustomerProductType; enabled: boolean }>(
      mergeMap(({ customerUserId, productId, enabled }) => {
        // Optimistically reflect the new state (also in the overview copy, which drives the
        // sidebar products badge); revert on error so the toggle never drifts from the backend.
        const patchProducts = (enabledValue: boolean) => {
          const overview = store.overview();
          return {
            products: mapProductEnabled(store.products(), productId, enabledValue),
            overview: overview ? { ...overview, Products: mapProductEnabled(overview.Products ?? [], productId, enabledValue) } : null,
          };
        };

        updateState(store, events.UPDATE.REQUEST, patchProducts(enabled), store.setLoading(StoreRequest.Update));
        return partnerPortalService.putPartnerCustomerProductEndpointSetProductEnabled({ customerUserId, productId, enabled }).pipe(
          tapResponse({
            next: () => updateState(store, events.UPDATE.SUCCESS, store.setFinished(StoreRequest.Update)),
            error: (error: ApiErrorResponse) => {
              updateState(store, events.UPDATE.ERROR, patchProducts(!enabled), store.setError(StoreRequest.Update, error));
              toastr.danger('The product could not be updated and was reverted to its previous state. Please try again.');
            },
          }),
        );
      }),
    ),

    downloadInvoicePdf: rxMethod<{ customerUserId: string; billingRecordId: number }>(
      switchMap(({ customerUserId, billingRecordId }) => {
        updateState(store, events.BILLING_PDF.REQUEST);
        return partnerPortalService
          .getPartnerCustomerBillingPdfEndpointGetBillingPdf({ customerUserId, billingRecordId }, 'body', false, {
            httpHeaderAccept: 'application/pdf' as unknown as undefined,
          })
          .pipe(
            tapResponse({
              next: (data: Blob) => {
                updateState(store, events.BILLING_PDF.SUCCESS);
                downloadFile(data, `invoice-${billingRecordId}.pdf`);
              },
              error: () => {
                updateState(store, events.BILLING_PDF.ERROR);
                toastr.danger('The invoice PDF could not be downloaded. Please try again.');
              },
            }),
          );
      }),
    ),

    accessAccount: rxMethod<string | null>(
      pipe(
        filter((customerUserId): customerUserId is string => !!customerUserId),
        switchMap((customerUserId) => {
          updateState(store, events.IMPERSONATE.REQUEST, store.setLoading(StoreRequest.Update));
          return partnerPortalService.postPartnerCustomerImpersonateEndpointImpersonate({ customerUserId }).pipe(
            tapResponse({
              next: ({ Token }) => {
                updateState(store, events.IMPERSONATE.SUCCESS, store.setFinished(StoreRequest.Update));
                if (!Token) {
                  return;
                }
                const dashboardWindow = window.open(`${environment.dashboardUrl}/auth/login?token=${Token}`, '_blank');
                if (!dashboardWindow) {
                  toastr.warning('Your browser blocked the dashboard window. Allow pop-ups for this site and try again.');
                }
              },
              error: (error: ApiErrorResponse) => {
                updateState(store, events.IMPERSONATE.ERROR, store.setError(StoreRequest.Update, error));
                toastr.danger('The customer account could not be accessed. Please try again.');
              },
            }),
          );
        }),
      ),
    ),

    // PP is a single-page app that never reloads on logout, so per-account state must be cleared
    // explicitly — otherwise the next user who logs in sees the previous account's data.
    reset(): void {
      updateState(store, events.RESET.SUCCESS, initialState);
    },
  })),
  withHooks({
    onInit({ loadCustomers }) {
      loadCustomers();
    },
  }),
);
